import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { get } from "@/lib/api";

type SpotifyAudioFeatures = {
  energy?: number;
  valence?: number;
  danceability?: number;
  tempo?: number;
  acousticness?: number;
  instrumentalness?: number;
  liveness?: number;
  speechiness?: number;
  loudness?: number;
  mode?: number;
  source?: string;
};

type TrackDetailState = {
  trackName?: string;
  artistName?: string;
};

const decimalFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const tempoFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

const featureList: Array<{ label: string; key: keyof SpotifyAudioFeatures }> = [
  { label: "Energy", key: "energy" },
  { label: "Valence", key: "valence" },
  { label: "Danceability", key: "danceability" },
  { label: "Acousticness", key: "acousticness" },
  { label: "Instrumentalness", key: "instrumentalness" },
  { label: "Liveness", key: "liveness" },
  { label: "Speechiness", key: "speechiness" },
  { label: "Tempo (BPM)", key: "tempo" },
  { label: "Loudness (dB)", key: "loudness" },
];

export default function TrackDetailPage() {
  const { trackId } = useParams<{ trackId: string }>();
  const location = useLocation();
  const { trackName, artistName } = (location.state as TrackDetailState) ?? {};
  const [features, setFeatures] = useState<SpotifyAudioFeatures | null>(null);
  const [loading, setLoading] = useState(true);
  const [audioAnalysis, setAudioAnalysis] = useState<Record<string, any> | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(true);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!trackId) {
      setError("This page needs a valid Spotify track ID.");
      setLoading(false);
      setAnalysisLoading(false);
      setAnalysisError(null);
      return;
    }
    setLoading(true);
    setError(null);
    setFeatures(null);
    setAudioAnalysis(null);
    setAnalysisError(null);
    setAnalysisLoading(true);
    get<SpotifyAudioFeatures>(`/spotify/track/${trackId}/features`)
      .then((data) => {
        setFeatures(data);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Unable to load track features right now.");
      })
      .finally(() => {
        setLoading(false);
      });
    get(`/spotify/track/${trackId}/audio-analysis`)
      .then((data) => {
        setAudioAnalysis(data);
      })
      .catch((err) => {
        setAnalysisError(err instanceof Error ? err.message : "Unable to load audio analysis right now.");
      })
      .finally(() => {
        setAnalysisLoading(false);
      });
  }, [trackId]);

  const displayTrackName = trackName ?? trackId ?? "Spotify Track";
  const displayArtistName = artistName ?? "Spotify Artist";

  const endpointList = useMemo(() => {
    if (!trackId) return [];
    return [
      {
        label: "Internal audio features cache",
        method: "GET",
        url: `/api/spotify/track/${trackId}/features`,
        description: "Cached energy, valence, tempo, and similar metrics we surface in this dashboard.",
      },
      {
        label: "Spotify track metadata",
        method: "GET",
        url: `https://api.spotify.com/v1/tracks/${trackId}`,
        description: "Master record for the song (name, artists, album, popularity, duration).",
      },
      {
        label: "Spotify audio features",
        method: "GET",
        url: `https://api.spotify.com/v1/audio-features/${trackId}`,
        description: "Normalized metrics such as energy, valence, danceability, tempo, and loudness.",
      },
      {
        label: "Spotify audio analysis",
        method: "GET",
        url: `https://api.spotify.com/v1/audio-analysis/${trackId}`,
        description: "Detailed segments, sections, bars, and tatums that power fine-grain visualizations.",
      },
    ];
  }, [trackId]);

  return (
    <section className="aa-page-shell space-y-6">
      <header className="flex flex-col gap-1">
        <p className="text-sm uppercase tracking-widest text-primary">Track analytics</p>
        <h1 className="text-3xl font-semibold text-foreground">{displayTrackName}</h1>
        <p className="text-sm text-muted-foreground">
          {displayArtistName} · Spotify ID <span className="font-mono text-xs text-sky-200">{trackId}</span>
        </p>
        <Link to="/marketing/music-analytics" className="text-sm text-sky-200 hover:underline">
          ← Back to Music Intelligence
        </Link>
      </header>

      {error && (
        <div className="rounded-md border border-red-500/40 bg-red-900/20 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="aa-glass-card bg-background/40 border-border">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Audio features</CardTitle>
            <p className="text-sm text-muted-foreground">{`Data powered by ${endpointList[2]?.label ?? "Spotify audio features"}`}</p>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading audio features…</p>
            ) : features ? (
              <div className="grid gap-3 md:grid-cols-2">
                {featureList.map((feature) => {
                  const rawValue = features[feature.key];
                  const formatted =
                    typeof rawValue === "number" && !Number.isNaN(rawValue)
                      ? feature.key === "tempo"
                        ? `${tempoFormatter.format(rawValue)} BPM`
                        : decimalFormatter.format(rawValue)
                      : "—";
                  return (
                    <div key={feature.label} className="rounded border border-border/50 bg-background/40 p-3">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{feature.label}</p>
                      <p className="text-2xl font-semibold text-foreground">{formatted}</p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No audio features available for this track yet.</p>
            )}
          </CardContent>
        </Card>

        <Card className="aa-glass-card bg-background/40 border-border">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Audio analysis summary</CardTitle>
            <p className="text-sm text-muted-foreground">Insights powered by Spotify’s audio-analysis endpoint</p>
          </CardHeader>
          <CardContent>
            {analysisLoading ? (
              <p className="text-sm text-muted-foreground">Loading analysis…</p>
            ) : analysisError ? (
              <p className="text-sm text-red-200">{analysisError}</p>
            ) : audioAnalysis ? (
              <div className="space-y-3 text-sm text-foreground/80">
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded border border-border/50 bg-background/40 p-3">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Key & mode</p>
                    <p className="text-2xl font-semibold text-foreground">
                      {keyToName(audioAnalysis.track?.key)} · {audioAnalysis.track?.mode === 1 ? "Major" : "Minor"}
                    </p>
                  </div>
                  <div className="rounded border border-border/50 bg-background/40 p-3">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Time signature</p>
                    <p className="text-2xl font-semibold text-foreground">{audioAnalysis.track?.time_signature ?? "—"}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-1 rounded border border-border/50 bg-background/40 p-3 text-xs uppercase tracking-wide text-muted-foreground">
                  <p>Sections: {audioAnalysis.sections?.length ?? 0}</p>
                  <p>Beats: {audioAnalysis.beats?.length ?? 0}</p>
                  <p>Segments: {audioAnalysis.segments?.length ?? 0}</p>
                  <p>Bars: {audioAnalysis.bars?.length ?? 0}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No audio analysis available for this track yet.</p>
            )}
          </CardContent>
        </Card>

        <Card className="aa-glass-card bg-background/40 border-border">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Spotify endpoints</CardTitle>
            <p className="text-sm text-muted-foreground">The HTTP routes you can hit for this track</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-foreground/80">
              {endpointList.map((endpoint) => (
                <a
                  key={endpoint.url}
                  href={endpoint.url}
                  target={endpoint.url.startsWith("http") ? "_blank" : undefined}
                  rel={endpoint.url.startsWith("http") ? "noreferrer" : undefined}
                  className="flex flex-col gap-0.5 rounded border border-border/50 bg-background/40 px-3 py-2 transition hover:border-primary/60"
                >
                  <span className="text-[11px] uppercase tracking-wide text-sky-400">{endpoint.method}</span>
                  <p className="font-medium text-foreground">{endpoint.label}</p>
                  <p className="text-xs text-muted-foreground">{endpoint.description}</p>
                  <p className="text-xs text-muted-foreground">{endpoint.url}</p>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
        <div className="aa-glass-card col-span-2 rounded border border-border/50 bg-background/40 px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">
          Batch audio features: call{" "}
          <span className="font-mono text-foreground/80">/spotify/track/audio-features?ids=&lt;comma-separated ids&gt;</span>{" "}
          (max 100 IDs) when you need multiple tracks at once.
        </div>
      </div>

    </section>
  );
}

const KEY_NAMES = ["C", "C♯/D♭", "D", "D♯/E♭", "E", "F", "F♯/G♭", "G", "G♯/A♭", "A", "A♯/B♭", "B"];

function keyToName(key?: number): string {
  if (typeof key !== "number" || key < 0 || key >= KEY_NAMES.length) {
    return "Unknown";
  }
  return KEY_NAMES[key];
}
